import { useEffect, useState } from 'react';
import { MdOutlineClose, MdExpandMore } from 'react-icons/md';

interface FAQModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface FAQItem {
    question: string;
    answer: string;
}

const faqs: FAQItem[] = [
    {
        question: 'Is Podus AI free?',
        answer: 'Podus AI is free to use for anybody willing to get legal insight.',
    },
    {
        question: 'How do I get started?',
        answer: 'Simply sign up for an account, and you can start chatting with our AI assistant. You can ask legal questions, create cases, and access various legal resources.',
    },
    {
        question: 'Does Podus AI replace Lawyers?',
        answer: 'Podus AI does not replace the position of lawyers in solving legal cases. It rather acts as a first aid for legal problems',
    },
    {
        question: 'Is PodusAI free?',
        answer: 'Yes. As of Q4 2025, PodusAI is completely free. Our mission is to reduce legal ignorance and empower Nigerians with accessible legal support while helping lawyers work more efficiently.',
    },
    {
        question: 'Is PodusAI only for lawyers?',
        answer: 'No. PodusAI serves both lawyers and everyday Nigerians. Whether you need to identify government agencies, understand your rights, or get initial legal guidance, PodusAI is built for active citizenship.',
    },
    {
        question: 'As a non-lawyer, what can PodusAI help with?',
        answer: 'PodusAI helps you report human rights violations and connect with pro bono lawyers; access Police PRO numbers for all 36 states in cases of brutality; Draft petitions, FOI requests, and complaint letters; Get legal guidance in plain terms.',
    },
    {
        question: 'Does PodusAI support Nigerian languages?',
        answer: 'Yes. You can ask questions in Yoruba, Hausa, and Igbo, and PodusAI will respond in the same language. We also offer voice features for users with visual or hearing disabilities.',
    },
    {
        question: 'Does PodusAI replace lawyers?',
        answer: "No. PodusAI won't represent you in court. Think of it as legal first aid helping you understand your situation and take informed next steps before engaging a lawyer.",
    },
    {
        question: 'Can PodusAI connect me with lawyers?',
        answer: "Yes. If your case involves human rights violations or gender-based abuse, we'll assess it and assign you a pro bono lawyer where applicable. Terms and conditions apply.",
    },
    {
        question: 'Can I trust PodusAI over general AI tools?',
        answer: 'Absolutely. Unlike general AI tools, PodusAI is trained specifically on Nigerian laws and precedents. PodusAI provides verifiable sources and citations for every legal reference, not just section numbers. You can download and confirm the accuracy yourself, protecting you from AI hallucinations.',
    },
    {
        question: 'How often do you update your legal database?',
        answer: 'We update quarterly. Our latest version 4.0 includes 800+ Nigerian legislations, 40,000+ Supreme & Appeal Court cases, Downloadable responses in Word format, State-specific Police PRO numbers & emergency helplines of national agencies.',
    },
    {
        question: 'How secure is my personal information?',
        answer: 'All personal data is encrypted following optimal data privacy practices. View our Privacy Police here.',
    },
    {
        question: 'How many prompts can I use per day?',
        answer: 'All users are allotted 6 free prompts daily.',
    },
    {
        question: 'How can I send feedback?',
        answer: "We welcome your feedback! Email us at hello@gavel.ng and we'll respond promptly.",
    },
    {
        question: 'What devices can I use PodusAI on?',
        answer: 'PodusAI is accessible on any device via our web app or WhatsApp (with fewer prompts). Click the chat icon in the top-left corner to get started.',
    },
];

export default function FAQModal({ isOpen, onClose }: FAQModalProps) {
    const [isClosing, setIsClosing] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
            setIsAnimating(false);
            setExpandedIndex(null);
        }, 300);
    };

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => setIsAnimating(true), 10);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const toggleFAQ = (index: number) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    return (
        <>
            {/* <div
                className={`fixed inset-0 z-500 bg-black/50 transition-opacity duration-300 ${
                    isClosing ? 'opacity-0' : isAnimating ? 'opacity-100' : 'opacity-0'
                }`}
                onClick={handleClose}
            /> */}

            <div
                className={`fixed inset-y-0 right-0 z-500 w-full max-w-md overflow-hidden bg-background shadow-2xl transition-transform duration-400 ease-out ${
                    isClosing ? 'translate-x-full' : isAnimating ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <div className="flex h-full flex-col">
                    {/* Header */}
                    <div className="h-[60px] flex items-center justify-between border-b border-black/10 px-6 py-5">
                        <h2 className="font-bold text-[#151515]">Frequently Asked Questions</h2>
                        <button
                            onClick={handleClose}
                            className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-black/5"
                            aria-label="Close FAQ"
                        >
                            <MdOutlineClose size={24} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-6 py-6">
                        <div className="space-y-4">
                            {faqs.map((faq, index) => (
                                <div
                                    key={index}
                                    className="rounded-lg border border-[#D2D2D2] bg-white transition-colors"
                                >
                                    <button
                                        onClick={() => toggleFAQ(index)}
                                        className="cursor-pointer flex w-full items-center justify-between px-4 py-4 text-left transition-colors hover:bg-gray-50"
                                    >
                                        <span className="text-sm font-semibold text-[#151515] pr-4">
                                            {faq.question}
                                        </span>
                                        <MdExpandMore
                                            size={24}
                                            className={`flex-shrink-0 transition-transform duration-200 ${
                                                expandedIndex === index ? 'rotate-180' : ''
                                            }`}
                                        />
                                    </button>
                                    <div
                                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                            expandedIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                                        }`}
                                    >
                                        <div className="border-t border-[#D2D2D2] px-4 py-4">
                                            <p className="text-[14px] leading-relaxed text-[#151515]">{faq.answer}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Contact Section */}
                        <div className="mt-8 rounded-lg border border-[#D2D2D2] bg-gray-50 p-6">
                            <h3 className="mb-3 text-lg font-semibold text-[#151515]">Still have questions?</h3>
                            <p className="mb-4 text-[14px] text-[#151515]">
                                Can't find the answer you're looking for? Please reach out to our support team.
                            </p>
                            <div className="space-y-3">
                                <a
                                    href="mailto:hello@gavel.ng"
                                    className="flex items-center gap-3 text-[14px] text-[#151515] hover:underline"
                                >
                                    <svg
                                        className="h-5 w-5 flex-shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                    </svg>
                                    hello@gavel.ng
                                </a>
                                <a
                                    href="tel:09031167894"
                                    className="flex items-center gap-3 text-[14px] text-[#151515] hover:underline"
                                >
                                    <svg
                                        className="h-5 w-5 flex-shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                        />
                                    </svg>
                                    0903 116 7894
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
